/**
 * PATH: packages/email-hub/src/feature/compose/DockTabStrip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPOSE-DOCKTABSTRIP-001
 * PURPOSE: The top bar of the single tabbed compose dock — one tab per
 *          open compose window (Gmail-style), a "+ new" affordance, and
 *          the dock-level window controls (minimize, pop-out, close all).
 *          Doubles as the drag handle for the whole dock.
 * WHY:     Operator ask (2026-06-15): the floating compose popup was a
 *          single narrow mini-window and stacking several overlapped.
 *          Re-shaped into ONE wider dock with a tab strip so multiple
 *          drafts/replies can be composed and switched between in place.
 * RELATED:
 *   - ComposeDock.tsx: owns the dock shell + drag/position + bodies.
 *   - composeDockStore.ts: window model + titleForIntent.
 */

import {
  ChevronUp, ExternalLink, Maximize2, Minimize2, Plus, Shrink, X,
} from 'lucide-react'
import { titleForIntent, type ComposeIntent } from './composeDockStore'

export interface DockTab {
  id: string
  intent: ComposeIntent
  /** Visible label — typed subject when present, else the intent title. */
  label: string
}

interface DockTabStripProps {
  tabs: DockTab[]
  activeId: string | null
  isMinimized: boolean
  /** FR (operator 2026-07-17): in-place expand — the dock grows to
   *  near-full viewport WITHOUT navigating away (pop-out closes the
   *  dock and re-opens the draft as a page, losing the reading
   *  context the operator was replying from). */
  isMaximized: boolean
  isMobile: boolean
  barHeight: number
  onSelect: (id: string) => void
  onCloseTab: (id: string) => void
  onNewTab: () => void
  onMinimizeToggle: () => void
  onMaximizeToggle: () => void
  onPopOut: () => void
  onCloseAll: () => void
  onDragStart: (e: React.PointerEvent) => void
}

/** Stop a pointer-down on an interactive control from starting a dock
 *  drag (the strip background is the drag handle). */
function swallowPointer(e: React.PointerEvent) {
  e.stopPropagation()
}

export default function DockTabStrip({
  tabs, activeId, isMinimized, isMaximized, isMobile, barHeight,
  onSelect, onCloseTab, onNewTab,
  onMinimizeToggle, onMaximizeToggle, onPopOut, onCloseAll, onDragStart,
}: DockTabStripProps) {
  return (
    <div
      className="flex items-stretch justify-between gap-1 bg-foreground/95 text-background select-none cursor-grab active:cursor-grabbing"
      style={{ height: barHeight }}
      onPointerDown={onDragStart}
    >
      {/* Tabs (scrollable) + new-tab button */}
      <div
        className="flex items-stretch gap-1 min-w-0 flex-1 overflow-x-auto px-1.5"
        role="tablist"
        aria-label="Open compose drafts"
      >
        {tabs.map((t) => {
          const isActive = t.id === activeId
          return (
            <div
              key={t.id}
              className={`group flex items-center gap-1 my-1 pl-2.5 pr-1 rounded-md max-w-[200px] flex-shrink-0 transition-colors ${
                isActive
                  ? 'bg-background text-foreground'
                  : 'bg-background/15 text-background hover:bg-background/25'
              }`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={titleForIntent(t.intent)}
                onPointerDown={swallowPointer}
                onClick={(e) => { e.stopPropagation(); onSelect(t.id) }}
                className="min-w-0 text-xs font-medium leading-tight truncate py-1"
                title={t.label}
              >
                {t.label}
              </button>
              <button
                type="button"
                onPointerDown={swallowPointer}
                onClick={(e) => { e.stopPropagation(); onCloseTab(t.id) }}
                className={`p-0.5 rounded flex-shrink-0 ${
                  isActive ? 'hover:bg-foreground/10' : 'hover:bg-background/30'
                }`}
                aria-label={`Close ${titleForIntent(t.intent)}`}
                title="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        })}
        <button
          type="button"
          onPointerDown={swallowPointer}
          onClick={(e) => { e.stopPropagation(); onNewTab() }}
          className="flex items-center gap-1 my-1 px-2 rounded-md text-xs font-medium flex-shrink-0 hover:bg-background/20"
          aria-label="New message"
          title="New message"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">New</span>
        </button>
      </div>

      {/* Dock-level window controls */}
      <div className="flex items-center gap-1 flex-shrink-0 pr-2">
        {!isMobile && (
          <button
            type="button"
            onPointerDown={swallowPointer}
            onClick={(e) => { e.stopPropagation(); onMinimizeToggle() }}
            className="p-1 rounded hover:bg-background/20"
            aria-label={isMinimized ? 'Restore compose window' : 'Minimize compose window'}
            title={isMinimized ? 'Restore' : 'Minimize'}
          >
            {isMinimized
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <Minimize2 className="h-3.5 w-3.5" />}
          </button>
        )}
        {!isMobile && (
          <button
            type="button"
            onPointerDown={swallowPointer}
            onClick={(e) => { e.stopPropagation(); onMaximizeToggle() }}
            className="p-1 rounded hover:bg-background/20"
            aria-label={isMaximized ? 'Restore compose window size' : 'Expand compose window'}
            title={isMaximized ? 'Restore size' : 'Expand'}
          >
            {isMaximized
              ? <Shrink className="h-3.5 w-3.5" />
              : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        )}
        <button
          type="button"
          onPointerDown={swallowPointer}
          onClick={(e) => { e.stopPropagation(); onPopOut() }}
          className="p-1 rounded hover:bg-background/20"
          aria-label="Open the active draft as a full page"
          title="Open as full page"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onPointerDown={swallowPointer}
          onClick={(e) => { e.stopPropagation(); onCloseAll() }}
          className="p-1 rounded hover:bg-background/20"
          aria-label="Close compose window"
          title="Close all drafts"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
